import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, CircularProgress, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Link as MuiLink, ToggleButton, ToggleButtonGroup,
  Tooltip, Button, Checkbox, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SavingsIcon from '@mui/icons-material/Savings';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RemoveIcon from '@mui/icons-material/Remove';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend,
} from 'recharts';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';

const LOOKBACK_OPTIONS = [
  { value: 1, label: '1Y' },
  { value: 2, label: '2Y' },
  { value: 5, label: '5Y' },
  { value: 10, label: '10Y' },
  { value: 30, label: 'Max' },
];

const SERIES_COLORS = {
  AMBSL: '#1f2937',
  M1SL: '#2563eb',
  WM1NS: '#3b82f6',
  M2SL: '#059669',
  WM2NS: '#10b981',
  MZMSL: '#8b5cf6',
  WMZMNS: '#a78bfa',
  MABMM301USM189S: '#7c3aed',
  MABMM301EZM189S: '#f59e0b',
  MABMM301JPM189S: '#06b6d4',
  MABMM301CAM189S: '#ec4899',
  MABMM401GBM189S: '#dc2626',
};

const DEFAULT_SELECTED = ['AMBSL', 'M1SL', 'M2SL', 'MABMM301USM189S', 'MABMM401GBM189S'];

const MONEY_SUPPLY_EXPLANATIONS = [
  {
    key: 'M0',
    label: 'M0 / Monetary Base',
    text: 'Most liquid base money: physical currency plus bank reserves at the central bank. Not all M0 becomes spendable money in the economy.',
  },
  {
    key: 'M1',
    label: 'M1',
    text: 'Narrow money: cash in circulation and highly liquid checkable deposits used directly for transactions.',
  },
  {
    key: 'M2',
    label: 'M2',
    text: 'Broad household/business money: M1 plus savings deposits, small time deposits, and retail money-market balances.',
  },
  {
    key: 'M3',
    label: 'M3 (where published)',
    text: 'Broader than M2 by adding larger, less-liquid institutional funding components. U.S. official M3 was discontinued; OECD broad-money proxies are used here.',
  },
  {
    key: 'M4',
    label: 'M4 (UK)',
    text: 'Very broad UK money aggregate that includes additional wholesale and institutional deposits beyond consumer-focused measures.',
  },
  {
    key: 'MZM',
    label: 'MZM (Money Zero Maturity)',
    text: 'Money available at par on demand (zero maturity), often used to gauge immediately spendable liquidity in the system.',
  },
];

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return d; }
};

const fmtLevel = (v) => {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return '—';
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const fmtCompact = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e12) return `${(n / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
  return n.toFixed(2);
};

const DeltaCell = ({ latest, previous }) => {
  if (latest === null || latest === undefined || previous === null || previous === undefined) {
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  }
  const diff = Number(latest) - Number(previous);
  if (!Number.isFinite(diff) || Math.abs(diff) < 1e-9) {
    return (
      <Stack direction="row" alignItems="center" spacing={0.25}>
        <RemoveIcon fontSize="small" color="disabled" />
        <Typography variant="body2" color="text.secondary">0</Typography>
      </Stack>
    );
  }

  const pct = Number(previous) !== 0 ? (diff / Math.abs(Number(previous))) * 100 : null;
  const up = diff > 0;

  return (
    <Stack direction="row" alignItems="center" spacing={0.25}>
      {up ? (
        <ArrowDropUpIcon fontSize="small" sx={{ color: 'success.main' }} />
      ) : (
        <ArrowDropDownIcon fontSize="small" sx={{ color: 'error.main' }} />
      )}
      <Typography variant="body2" sx={{ color: up ? 'success.main' : 'error.main' }}>
        {up ? '+' : ''}{fmtCompact(diff)}
        {Number.isFinite(pct) ? ` (${up ? '+' : ''}${pct.toFixed(2)}%)` : ''}
      </Typography>
    </Stack>
  );
};

const MoneySupply = () => {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [years, setYears] = useState(10);
  const [selectedSeries, setSelectedSeries] = useState(DEFAULT_SELECTED);
  const [reloadKey, setReloadKey] = useState(0);
  const [glossaryOpen, setGlossaryOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(`${API_BASE_URL}/api/money-supply?years=${years}`)
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body?.error || `HTTP ${r.status}`);
        return body;
      })
      .then((body) => {
        if (!cancelled) {
          setData(body);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load money supply data');
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [years, reloadKey]);

  const chartData = useMemo(() => {
    if (!data?.series) return [];
    const activeSeries = data.series.filter((s) => selectedSeries.includes(s.id));
    if (activeSeries.length === 0) return [];

    // Anchor lookback to the newest datapoint returned so stale upstream series don't skew the window.
    let newestDateMs = null;
    activeSeries.forEach((s) => {
      for (const obs of s.observations || []) {
        const value = Number(obs.value);
        if (!Number.isFinite(value)) continue;
        const dateMs = Date.parse(obs.date);
        if (!Number.isFinite(dateMs)) continue;
        if (newestDateMs === null || dateMs > newestDateMs) {
          newestDateMs = dateMs;
        }
      }
    });

    if (newestDateMs === null) return [];

    const isMaxRange = years >= 30;
    let lookbackStartMs = null;
    if (!isMaxRange) {
      const lookbackStart = new Date(newestDateMs);
      lookbackStart.setUTCFullYear(lookbackStart.getUTCFullYear() - years);
      lookbackStartMs = lookbackStart.getTime();
    }

    const byDate = new Map();

    activeSeries.forEach((s) => {
      for (const obs of s.observations || []) {
        const value = Number(obs.value);
        if (!Number.isFinite(value)) continue;

        const dateMs = Date.parse(obs.date);
        if (!Number.isFinite(dateMs)) continue;
        if (lookbackStartMs !== null && dateMs < lookbackStartMs) continue;

        if (!byDate.has(obs.date)) byDate.set(obs.date, { date: obs.date });
        byDate.get(obs.date)[s.id] = value;
      }
    });

    return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [data, selectedSeries, years]);

  const toggleSeries = (id) => {
    setSelectedSeries((prev) => (
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    ));
  };

  const renderSortedTooltip = ({ active, payload, label }) => {
    if (!active || !Array.isArray(payload) || payload.length === 0) return null;

    const sortedPayload = [...payload]
      .filter((item) => item && item.value !== null && item.value !== undefined)
      .sort((a, b) => Number(b.value) - Number(a.value));

    if (sortedPayload.length === 0) return null;

    return (
      <Box
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(17,24,39,0.96)' : '#fff',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          px: 1.5,
          py: 1,
          minWidth: 240,
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {fmtDate(label)}
        </Typography>
        <Stack spacing={0.3}>
          {sortedPayload.map((entry) => (
            <Typography
              key={`${entry.dataKey}-${label}`}
              variant="body2"
              sx={{ color: entry.color || 'text.primary', fontWeight: 500 }}
            >
              {entry.name} : {fmtLevel(entry.value)}
            </Typography>
          ))}
        </Stack>
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <SavingsIcon color="primary" />
            <Typography variant="h5" component="h2">Money Supply</Typography>
            <Tooltip title="What do M1/M2/M3/M4 mean?">
              <IconButton size="small" onClick={() => setGlossaryOpen(true)} aria-label="Money supply glossary">
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Monetary aggregates (M1, M2, M3 proxy, M4) from FRED. Layout mirrors the Rates tab.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <ToggleButtonGroup
            size="small"
            exclusive
            color="primary"
            value={years}
            onChange={(_e, v) => { if (v) setYears(v); }}
          >
            {LOOKBACK_OPTIONS.map((opt) => (
              <ToggleButton key={opt.value} value={opt.value}>{opt.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Tooltip title="Refresh">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => setReloadKey((k) => k + 1)}
                disabled={loading}
              >
                Reload
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 4 }}>
          <CircularProgress size={20} /> <Typography>Loading money supply data...</Typography>
        </Box>
      )}

      {error && !loading && (
        <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
      )}

      {!loading && !error && data && (
        <>
          <Paper variant="outlined" sx={{ mb: 3 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Show</TableCell>
                    <TableCell>Series</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Latest</TableCell>
                    <TableCell>As of</TableCell>
                    <TableCell align="right">1-period change</TableCell>
                    <TableCell>Source</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.series.map((s) => (
                    <TableRow key={s.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedSeries.includes(s.id)}
                          onChange={() => toggleSeries(s.id)}
                          sx={{ color: SERIES_COLORS[s.id], '&.Mui-checked': { color: SERIES_COLORS[s.id] } }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ width: 10, height: 10, bgcolor: SERIES_COLORS[s.id], borderRadius: '2px' }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{s.label}</Typography>
                          <Chip size="small" variant="outlined" label={s.id} sx={{ fontFamily: 'monospace' }} />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={s.category} variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtLevel(s.latest?.value)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{fmtDate(s.latest?.date)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <DeltaCell latest={s.latest?.value} previous={s.previous?.value} />
                      </TableCell>
                      <TableCell>
                        <MuiLink
                          href={`https://fred.stlouisfed.org/series/${s.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.85rem' }}
                        >
                          FRED <OpenInNewIcon fontSize="inherit" />
                        </MuiLink>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6">
                Historical money supply — {years >= 30 ? 'max range' : `last ${years} year${years === 1 ? '' : 's'}`}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={() => setSelectedSeries(DEFAULT_SELECTED)}>Reset</Button>
                <Button size="small" onClick={() => setSelectedSeries(data.series.map((s) => s.id))}>Select all</Button>
                <Button size="small" onClick={() => setSelectedSeries([])}>Clear</Button>
              </Stack>
            </Stack>

            {selectedSeries.length === 0 ? (
              <Alert severity="info">Tick one or more series in the table above to plot them.</Alert>
            ) : (
              <Box sx={{ height: { xs: 320, sm: 460 } }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} minTickGap={32} />
                    <YAxis tick={{ fontSize: 11, fill: theme.palette.text.secondary }} tickFormatter={(v) => fmtCompact(v)} domain={['auto', 'auto']} />
                    <RTooltip content={renderSortedTooltip} />
                    <Legend wrapperStyle={{ fontSize: '0.8rem' }} />
                    {data.series
                      .filter((s) => selectedSeries.includes(s.id))
                      .map((s) => (
                        <Line
                          key={s.id}
                          type="monotone"
                          dataKey={s.id}
                          name={s.label}
                          stroke={SERIES_COLORS[s.id]}
                          dot={false}
                          strokeWidth={1.75}
                          connectNulls
                          isAnimationActive={false}
                        />
                      ))}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Data: {data.source}. Generated at {fmtDate(data.generatedAt)} ({data.cached ? 'served from cache' : 'fresh'}).
          </Typography>
        </>
      )}

      <Dialog open={glossaryOpen} onClose={() => setGlossaryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <InfoOutlinedIcon color="primary" />
            <span>Money Supply — What The M Aggregates Mean</span>
          </Stack>
          <IconButton onClick={() => setGlossaryOpen(false)} size="small" aria-label="Close glossary">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            {MONEY_SUPPLY_EXPLANATIONS.map((item) => (
              <Box key={item.key}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                  <Chip size="small" variant="outlined" label={item.key} sx={{ fontFamily: 'monospace' }} />
                  <Typography variant="subtitle2">{item.label}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {item.text}
                </Typography>
              </Box>
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Note: not every country publishes every M aggregate with the same definition.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGlossaryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MoneySupply;
